import { Box, BoxProps, Modal, Skeleton, styled, Tooltip } from "@mui/material";
import React, { FC, ReactNode, Ref, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { MdsHideImage } from "@sinch/icons";

const ImageBoxContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "exactSize" && prop !== "imgWidth" && prop !== "imgHeight" && prop !== "clickable",
})<BoxProps & Omit<ImageBoxProps, "render"> & { clickable: boolean }>(
  ({ theme, imgWidth, imgHeight, disabled, exactSize, clickable }) => ({
    position: "relative",
    height: exactSize ? undefined : "12rem",
    display: "flex",
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    borderColor: theme.palette.grey[400],
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: `${theme.shape.borderRadius}px !important`,
    cursor: clickable ? "zoom-in" : undefined,
    "&>img": {
      width: exactSize ? undefined : `${imgWidth}px`,
      height: exactSize ? undefined : `${imgHeight}px`,
      minWidth: "100%",
      maxWidth: "100%",
      minHeight: "100%",
      objectFit: "cover",
      opacity: disabled ? 0.3 : 1,
    },
  })
);

export interface ImageBoxProps {
  render: (props: { width: number; height: number; setRef: Ref<HTMLImageElement> }) => ReactNode;
  imgWidth: number;
  imgHeight: number;
  disabled: boolean;
  exactSize?: boolean;
  lightbox?: boolean;
}

type ImageStatus = "loading" | "loaded" | "error";

export const ImageBox: FC<ImageBoxProps> = ({ render, imgWidth, imgHeight, disabled, exactSize, lightbox }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ImageStatus>("loading");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState("");
  const controllerRef = useRef<AbortController | null>(null);

  const setImageRef = useCallback((node: HTMLImageElement | null) => {
    controllerRef.current?.abort();
    controllerRef.current = null;

    if (node === null) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    const captureSrc = () => setLightboxSrc(node.currentSrc || node.src || "");

    if (node.complete && node.naturalWidth > 0) {
      setStatus("loaded");
      captureSrc();
    } else {
      setStatus("loading");
    }

    node.addEventListener("loadstart", () => setStatus("loading"), { signal });
    node.addEventListener(
      "load",
      () => {
        setStatus("loaded");
        captureSrc();
      },
      { signal }
    );
    node.addEventListener("error", () => setStatus("error"), { signal });
  }, []);

  const isClickable = lightbox === true && status === "loaded" && !disabled;

  return (
    <>
      <ImageBoxContainer
        clickable={isClickable}
        disabled={disabled}
        exactSize={exactSize}
        imgHeight={imgHeight}
        imgWidth={imgWidth}
        onClick={(event) => {
          if (isClickable) {
            event.stopPropagation();
            setLightboxOpen(true);
          }
        }}
      >
        {status === "loading" && (
          <Skeleton
            animation="wave"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            variant="rectangular"
          />
        )}
        {status === "error" && (
          <Tooltip title={t("Error.imageNotLoaded")}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                px: 1,
              }}
            >
              <MdsIcon color="disabled" fontSize="large" icon={MdsHideImage} />
            </Box>
          </Tooltip>
        )}
        {/* setImageRef is a stable callback ref, not a ref value read during render */}
        {/* oxlint-disable-next-line react-hooks-js/refs */}
        {render({ width: imgWidth, height: imgHeight, setRef: setImageRef })}
      </ImageBoxContainer>
      {lightbox === true && (
        <Modal
          onClose={() => setLightboxOpen(false)}
          open={lightboxOpen}
          slotProps={{ backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.85)" } } }}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Box
            alt=""
            component="img"
            onClick={() => setLightboxOpen(false)}
            src={lightboxSrc}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: 1,
              outline: "none",
              cursor: "zoom-out",
            }}
            tabIndex={-1}
          />
        </Modal>
      )}
    </>
  );
};
